import { useState } from "react";
import { isRunningAd } from "@/services/contentProgram.service";
import { useNavigate } from "react-router-dom";
import { IconExternalLink } from "@tabler/icons-react";

import { Button } from "@/components/ui/button";
import { DialogNotice, DialogTextButton } from "@/components/common/ActionDialog";
import { StatusText } from "@/components/dashboard/dashboard-card";
import { AsideRow } from "@/components/settings/settings-kit";
import { PLACEMENT_LABEL } from "@/components/content-programs/media-tile";
import { SubmissionDialog } from "@/components/content-programs/submission-dialog";
import { submissionStatus } from "@/components/brand/content-programs/submission-status";
import type { MetaAspectRatio } from "@/config/contentProgramUpload";
import { formatDate } from "@/utils/formatters";
import { submissionBrandLabel, type AffiliateSubmissionRow } from "./AffiliateSubmissionPortraitTile";

/**
 * What the affiliate sees for one submission: the creative, where it is in
 * review, the brand's note, and the one thing they can do about it.
 */
export function AffiliateSubmissionDialog({
  submission,
  ratioLabel,
  onOpenChange,
  onWithdraw,
  withdrawing,
}: {
  submission: AffiliateSubmissionRow | null;
  ratioLabel?: MetaAspectRatio | null;
  onOpenChange: (open: boolean) => void;
  onWithdraw: (id: string) => void;
  withdrawing: boolean;
}) {
  const navigate = useNavigate();
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);

  if (!submission) return null;

  const isVideo = submission.mediaType === "VIDEO";
  const title = submission.title?.trim() || "Untitled";
  const status = submissionStatus(submission.status);
  const program = submission.metaAdsCampaignProgram;
  const canWithdraw = submission.status === "PENDING";
  const canResubmit = submission.status === "CHANGE_REQUESTED";
  const stripScheme = (url: string) => url.replace(/^https?:\/\//, "");

  const whatNext =
    submission.status === "PENDING"
      ? "The brand is reviewing it"
      : submission.status === "CHANGE_REQUESTED"
        ? "Update it and resubmit"
        : submission.status === "APPROVED"
          ? "The brand can run it as a Meta ad"
          : isRunningAd(submission)
            ? `You earn ${program.commissionRate}% on sales from this ad`
            : submission.status === "REJECTED"
              ? "You can submit something new"
              : null;

  return (
    <SubmissionDialog
      open={submission !== null}
      onOpenChange={(open) => {
        if (!open) setConfirmWithdraw(false);
        onOpenChange(open);
      }}
      srTitle={title}
      mediaUrl={submission.mediaUrl}
      mediaType={isVideo ? "VIDEO" : "IMAGE"}
      ratioLabel={ratioLabel}
      header={
        <div className="min-w-0">
          <div className="truncate text-[15px] font-semibold text-foreground">{title}</div>
          <div className="truncate text-xs text-muted-foreground">
            {program.title} for {submissionBrandLabel(submission)}
          </div>
        </div>
      }
      footer={
        confirmWithdraw ? (
          <>
            <DialogTextButton onClick={() => setConfirmWithdraw(false)} disabled={withdrawing}>
              Keep it
            </DialogTextButton>
            <Button type="button" variant="destructive" onClick={() => onWithdraw(submission.id)} disabled={withdrawing}>
              Withdraw
            </Button>
          </>
        ) : canWithdraw ? (
          <DialogTextButton onClick={() => setConfirmWithdraw(true)} className="hover:text-destructive">
            Withdraw submission
          </DialogTextButton>
        ) : canResubmit ? (
          <Button
            type="button"
            onClick={() => navigate(`/affiliate/content-programs/${program.id}/submit?resubmit=${submission.id}`)}
          >
            Resubmit
          </Button>
        ) : undefined
      }
    >
      <AsideRow label="Status">
        <StatusText status={submission.status} label={status.label} />
      </AsideRow>
      <AsideRow label="Commission">{program.commissionRate}%</AsideRow>
      <AsideRow label="Format">{isVideo ? "Video" : "Image"}</AsideRow>
      <AsideRow label="Size">{ratioLabel ? `${ratioLabel}, ${PLACEMENT_LABEL[ratioLabel].toLowerCase()}` : "Unknown"}</AsideRow>
      <AsideRow label="Products">
        {submission.products?.length
          ? submission.products.length === 1
            ? submission.products[0].title
            : `${submission.products.length} products`
          : "None tagged"}
      </AsideRow>
      <AsideRow label="Submitted">{formatDate(submission.createdAt)}</AsideRow>
      {submission.updatedAt !== submission.createdAt && <AsideRow label="Updated">{formatDate(submission.updatedAt)}</AsideRow>}
      {submission.destinationUrl && (
        <AsideRow label="Links to">
          <a
            href={submission.destinationUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex max-w-full items-center gap-1 underline-offset-4 hover:underline"
          >
            <span className="truncate">{stripScheme(submission.destinationUrl)}</span>
            <IconExternalLink size={12} stroke={1.75} className="shrink-0" aria-hidden />
          </a>
        </AsideRow>
      )}

      <div className="mt-3 space-y-2">
        {submission.reviewMessage && (
          <DialogNotice tone={submission.status === "REJECTED" ? "error" : "warning"}>
            Note from {submissionBrandLabel(submission)}: {submission.reviewMessage}
          </DialogNotice>
        )}
        {confirmWithdraw && (
          <DialogNotice tone="warning">Withdrawing removes it from the brand's review queue. You can submit again later.</DialogNotice>
        )}
        {whatNext && !confirmWithdraw && <p className="text-xs text-muted-foreground">{whatNext}</p>}
      </div>
    </SubmissionDialog>
  );
}
